// The Article manifest + resolve seam: a pure seam parallel to the catalog seam. An ordered list of
// Article metadata is the single source of truth for the Compendium — the route set, the prerender list, the
// (future) top tab-strip, and the (future) landing index all derive from it. The functions are pure
// and take their entries explicitly, so they test with light fixtures and a backend source could feed
// the same shape later. Adding an Article is a Markdown file plus one manifest line (see articles.ts).

/** What a manifest line declares about one Article: its route slug, title, nav order, and OG blurb. */
export interface ArticleMeta {
  /** URL slug; the Article is routed at `/thecore/<slug>`. */
  slug: string;
  /** Display + document title. */
  title: string;
  /** Position in the Compendium nav and landing index; ascending. */
  navOrder: number;
  /** One-line summary for the landing index and the link-preview card. */
  description: string;
}

/** A manifest Article with its Markdown compiled to an HTML body (compiled once, at build time). */
export interface Article extends ArticleMeta {
  /** The compiled HTML body, used identically by the prerender and the runtime route. */
  html: string;
}

/** The Compendium root and an Article's canonical route. `/thecore` is the (v2-02) landing. */
export const COMPENDIUM_ROUTE = "/thecore";
export const articleRoutePath = (slug: string): string => `${COMPENDIUM_ROUTE}/${slug}`;

/** The Article claiming a slug, or undefined when none does (a not-found `/thecore/<unknown>`). */
export function resolveArticle<T extends { slug: string }>(
  articles: readonly T[],
  slug: string,
): T | undefined {
  return articles.find((article) => article.slug === slug);
}

/** The Articles in nav order (ascending navOrder), the order the Compendium presents them. */
export function articleNav<T extends ArticleMeta>(articles: readonly T[]): T[] {
  return [...articles].sort((a, b) => a.navOrder - b.navOrder);
}

/** One Compendium tab-strip entry: a label and the route it targets. */
export interface CompendiumTab {
  label: string;
  to: string;
}

/**
 * The Compendium tab-strip: a fixed leading Overview tab (the landing) then the Articles in nav order. The
 * Article tabs are manifest-derived; Overview is the landing's own tab, so the strip always has a tab
 * for wherever you are in the Compendium (no orphan single tab while only one Article exists).
 */
export function compendiumTabs<T extends ArticleMeta>(articles: readonly T[]): CompendiumTab[] {
  return [
    { label: "Overview", to: COMPENDIUM_ROUTE },
    ...articleNav(articles).map((article) => ({
      label: article.title,
      to: articleRoutePath(article.slug),
    })),
  ];
}
